import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { getCategoryByCode } from "../../services/categoryService";

/**
 * Componente reutilizable para seleccionar ítems de una categoría por código.
 * Ejemplos de códigos: "document_type", "gender", "academic_level", etc.
 *
 * Props:
 * - categoryCode: Código único de la categoría (requerido).
 * - value: GUID del item seleccionado (opcional).
 * - onChange: callback que retorna SOLO el GUID (string) del item seleccionado (requerido).
 * - isDisabled: deshabilita el select (opcional).
 * - placeholder: texto del placeholder (opcional).
 * - showCode: si muestra el (CODE) en la etiqueta (opcional).
 * - onlyActive: filtra ítems activos (opcional).
 *
 * NUEVO (para casos avanzados como NIT/Jurídica):
 * - onSelectItem: callback que retorna el item crudo seleccionado (item completo del backend).
 * - onItemsLoaded: callback que retorna la lista cruda de items cuando cargan desde la API.
 */
const SelectCategory = ({
  categoryCode,
  value, // GUID del item seleccionado
  onChange,
  isDisabled = false,
  placeholder = "Seleccione una opción...",
  showCode = true,
  onlyActive = true,

  // NUEVOS
  onSelectItem,
  onItemsLoaded,
}) => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [options, setOptions] = useState([]);
  const [rawItems, setRawItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const checkDarkMode = () =>
    document.body.classList.contains("dark-mode") ||
    document.querySelector(".app")?.classList.contains("dark-mode");

  useEffect(() => {
    setIsDarkMode(checkDarkMode());
    const targetNode = document.querySelector(".app") || document.body;
    const observer = new MutationObserver(() => setIsDarkMode(checkDarkMode()));
    observer.observe(targetNode, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchCategoryItems = async () => {
      setLoading(true);
      try {
        const response = await getCategoryByCode(categoryCode);
        let items = response?.data?.items || [];
        if (onlyActive) items = items.filter((i) => i.isActive);

        setRawItems(items);
        onItemsLoaded?.(items); //NUEVO: devuelve items crudos

        const mapped = items.map((item) => ({
          value: item.id, // GUID
          label: showCode ? `${item.name} (${item.code})` : item.name,
          raw: item, // item completo (id, code, name, etc.)
        }));

        setOptions(mapped);
      } catch (err) {
        console.error("Error al cargar la categoría:", err);
        setRawItems([]);
        setOptions([]);
        onItemsLoaded?.([]); //NUEVO: devuelve vacío si falla
      } finally {
        setLoading(false);
      }
    };

    if (categoryCode) fetchCategoryItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryCode, onlyActive, showCode]);

  const selectedOption = useMemo(() => {
    if (!value) return null;
    return options.find((o) => o.value === value) || null;
  }, [value, options]);

  // Si el value cambia externamente (ej: setFieldValue desde el formulario),
  // notificamos el item crudo seleccionado (si el padre lo necesita).
  useEffect(() => {
    if (!onSelectItem) return;
    if (!value) {
      onSelectItem(null);
      return;
    }

    const foundRaw =
      rawItems.find((i) => i.id === value) || selectedOption?.raw || null;

    onSelectItem(foundRaw);
  }, [value, rawItems, selectedOption, onSelectItem]);

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: 38,
      backgroundColor: isDarkMode ? "#1e1e2f" : "#fff",
      borderColor: state.isFocused
        ? "#6c63ff"
        : isDarkMode
        ? "#444"
        : "#ced4da",
      boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(108,99,255,.25)" : "none",
      color: isDarkMode ? "#fff" : "#000",
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: isDarkMode ? "#1e1e2f" : "#fff",
      color: isDarkMode ? "#fff" : "#000",
      zIndex: 20000,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 20000,
    }),
    singleValue: (provided) => ({
      ...provided,
      color: isDarkMode ? "#fff" : "#000",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#6c63ff"
        : state.isFocused
        ? isDarkMode
          ? "#2c2c3e"
          : "#f0f0f0"
        : isDarkMode
        ? "#1e1e2f"
        : "#fff",
      color: isDarkMode ? "#fff" : "#000",
      cursor: "pointer",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: isDarkMode ? "#aaa" : "#888",
    }),
    input: (provided) => ({
      ...provided,
      color: isDarkMode ? "#fff" : "#000",
    }),
  };

  return (
    <Select
      styles={customStyles}
      placeholder={placeholder}
      isClearable
      isDisabled={isDisabled || loading}
      isLoading={loading}
      menuPortalTarget={document.body}
      options={options}
      value={selectedOption}
      onChange={(opt) => {
        // 1) Mantiene compatibilidad: solo GUID al formulario
        onChange(opt?.value || "");

        // 2) NUEVO: item crudo para reglas (code, etc.)
        onSelectItem?.(opt?.raw || null);
      }}
    />
  );
};

export default SelectCategory;
